import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const UploadDocumentModal = ({ isOpen, onClose, certificate }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen || !certificate) return null;

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!['application/pdf', 'image/jpeg', 'image/png']?.includes(file?.type)) {
        alert('Only PDF, JPEG, and PNG files are allowed');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsUploading(false);
            onClose();
            setSelectedFile(null);
            setUploadProgress(0);
          }, 500);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-2xl" onClick={(e) => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-foreground font-heading">Upload Certificate Document</h2>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="p-4 rounded-lg bg-input border border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground font-caption mb-1 block">Property ID</label>
                <p className="text-sm text-foreground font-data">{certificate?.propertyId}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground font-caption mb-1 block">Certificate Type</label>
                <p className="text-sm text-foreground">{certificate?.typeName}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground font-caption mb-3 block">Select Document</label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center transition-smooth hover:border-accent">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isUploading}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="h-16 w-16 rounded-lg bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={32} color="var(--color-accent)" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-muted-foreground">PDF, JPEG, PNG up to 10MB</p>
              </label>
            </div>
          </div>

          {selectedFile && (
            <div className="p-4 rounded-lg bg-input border border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name="FileText" size={20} color="var(--color-accent)" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{selectedFile?.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile?.size)}</p>
                  </div>
                </div>
                {!isUploading && (
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="h-8 w-8 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
                    aria-label="Remove file"
                  >
                    <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                  </button>
                )}
              </div>

              {isUploading && (
                <div>
                  <div className="h-2 bg-input rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">{uploadProgress}% uploaded</p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button variant="outline" fullWidth onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="default"
              fullWidth
              iconName="Upload"
              iconPosition="left"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              loading={isUploading}
            >
              Upload Document
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;